import type {ISchemaMapper} from '@igp/shared/redux/entity';
import {type Schema, schema} from 'normalizr';
import Entity = schema.Entity;
import type {
    AgentNegativeCarryoverReport,
    AgentPlayerReportingV2,
    AgentRefereeRevenueReport,
    BackpackItem,
    BonusCodeDetails,
    BoUser,
    BulkOperationResult,
    CountryLevelReport,
    DebtResponsibleMasterAgent,
    DebtTransferLog,
    Group,
    KeycloakLog,
    PayoutReport,
    PlayerBonus,
    PlayerEcologyWeeklyReport,
    PlayerReferral,
    ReferralLinkConfig,
    RepaymentLog,
    Transaction,
    UserProfile,
    WeeklySummaryExport,
} from '@igp/shared/contract/pam';
import type {Country} from '@igp/shared/features/country';
import type {P2PPlayerSettingsModel} from '@igp/shared/features/p2p-player-settings';
import type {UserActivitySummaryServiceModel} from '@igp/shared/features/user-activity-summary';
import {nameOf} from '@igp/shared/utils';

import {EntityType} from './types';

class SchemaMapper implements ISchemaMapper<EntityType> {
    private entitiesMap: Record<EntityType, Entity>;
    private entityIdMap: Record<EntityType, string | schema.SchemaFunction> = {
        UserProfile: nameOf<UserProfile>('uid'),
        Transaction: nameOf<Transaction>('transaction_id'),
        AgentLevelReportV2: nameOf<AgentRefereeRevenueReport>('row_id'),
        PlayerLevelReportV2: (value: AgentPlayerReportingV2) => `${value.uid}_${value.referrer_agent_id}`,
        Country: nameOf<Country>('iso2Code'),
        BulkOperation: (value: BulkOperationResult) => value.id,
        AgentProfile: nameOf<UserProfile>('uid'),
        NegaitveCarryoverReport: (i: AgentNegativeCarryoverReport) => i.id,
        NegativeCarryoverWeeklySummary: nameOf<WeeklySummaryExport>('agent_uid'),
        UserActivitySummary: nameOf<UserActivitySummaryServiceModel>('uid'),
        PlayerBonus: nameOf<PlayerBonus>('bonus_id'),
        BonusCode: nameOf<BonusCodeDetails>('bonus_id'),
        Backpack: nameOf<BackpackItem>('bonus_id'),
        PlayerReferral: (value: PlayerReferral) => `${value?.referrer_id}-${value?.referee?.uid}`,
        CountryLevelPartnerReport: (value: CountryLevelReport) => `${value.country}_${value.date_ts?.seconds}`,
        DebtResponsibleMasterAgents: (value: DebtResponsibleMasterAgent) => value.agent_id,
        DebtTransferLog: (value: DebtTransferLog) => value?.log_id,
        KeycloakLog: (value: KeycloakLog) => value?.log_id,
        RepaymentLog: (value: RepaymentLog) => value?.repayment_id,
        PayoutReport: (value: PayoutReport) => value?.payout_report_id,
        BoUser: (value: BoUser) => value?.id,
        BoUserGroup: (value: Group) => value?.id,
        P2PPlayerSettings: (value: P2PPlayerSettingsModel) => value.id,
        PlayerEcologyWeekly: (value: PlayerEcologyWeeklyReport) => value.row_id,
        ReferralLinkConfig: (value: ReferralLinkConfig) => `${value.aal_group_name}_${value.license ?? ''}`,
    };

    constructor() {
        this.initializeEntities();
    }

    public getSchema(entityType: EntityType): Entity {
        return this.entitiesMap[entityType];
    }

    public getSchemaDefinitions(entityType: EntityType): Schema {
        const schemaMap: Partial<Record<EntityType, Schema>> = {
            PlayerReferral: this.getPlayerReferralSchema(),
        };
        return schemaMap[entityType] ?? {};
    }

    private initializeEntities() {
        this.entitiesMap = {} as Record<EntityType, Entity>;

        Object.values<EntityType>(EntityType).forEach(type => {
            this.entitiesMap[type] = new Entity(type, {}, {idAttribute: this.entityIdMap[type]});
        });

        this.defineSchemas();
    }

    private defineSchemas(): void {
        this.entitiesMap.PlayerReferral.define(this.getPlayerReferralSchema());
    }

    private getPlayerReferralSchema(): Schema {
        return {
            referee: this.entitiesMap.UserProfile,
        };
    }
}

export const schemaMapper = new SchemaMapper();
